import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DownloadIcon from '@mui/icons-material/Download';
import useWorkflowLayout from '../hooks/useWorkflowLayout';
import StepNode from './StepNode';

export default function WorkflowVisualizer({ workflow, width = 1250, height = 600 }) {
    const result = useWorkflowLayout(workflow);
    const nodes = (result && result.nodes) || [];
    const edges = (result && result.edges) || [];

    const nodeById = new Map(nodes.map(n => [n.id, n]));
    const stepById = new Map((workflow.steps || []).map(s => [s.step_id, s]));

    const safeNum = arr => (arr && arr.length ? Math.max(...arr) : 0);
    const maxX = safeNum(nodes.map(n => n.x + (n.width || 0) + 40));
    const maxY = safeNum(nodes.map(n => n.y + (n.height || 0) + 40));
    const svgWidth = Math.max(width || 0, maxX || 0);
    const svgHeight = Math.max(height || 0, maxY || 0);

    const containerRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const dragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

    if (!workflow) return null;
    if (!nodes.length) {
        return (
            <Box sx={{ p: 2 }}>
                <em>No steps available to visualize for this workflow.</em>
            </Box>
        );
    }

    const renderEdgePath = (e) => {
        const from = nodeById.get(e.from);
        const to = nodeById.get(e.to);
        if (!from || !to) return null;
        // For vertical layout: default bottom-center -> top-center curve
        const startCenterX = from.x + from.width / 2;
        const startBottomY = from.y + from.height;
        const targetCenterX = to.x + to.width / 2;
        const targetTopY = to.y;

        let path;
        // if the edge goes upward (target above source), draw from left side to left side
        if (targetTopY < startBottomY) {
            if (e.action === 'backward') {
                const startX = from.x + from.width;
                const startY = from.y + from.height / 3;
                const endX = to.x + to.width;
                const endY = to.y + to.height / 2;
                // dynamic horizontal offset: consider horizontal distance and vertical delta
                const dx = Math.abs(endX - startX);
                const dy = Math.abs(endY - startY);
                // stronger curves for larger separations; floor at 80px
                const horizOffset = Math.max(80, dy / 3, dx * 0.6);
                const cp1x = startX + horizOffset;
                const cp2x = endX + horizOffset;

                path = `M ${startX} ${startY} C ${cp1x} ${startY} ${cp2x} ${endY} ${endX} ${endY}`;
            } else if (e.from === e.to) {
                const endX = to.x;
                const endY = to.y;
                const startX = from.x;
                const startY = from.y + from.height / 2.4;
                // const endX = to.x + to.width;
                // const endY = to.y + to.height;
                const horizOffset = Math.max(40, Math.abs(endY - startY) / 4);
                const cp1x = startX - horizOffset;
                const cp2x = endX - horizOffset;

                path = `M ${startX} ${startY} C ${cp1x} ${startY} ${cp2x} ${endY} ${endX} ${endY}`;
            } else {
                const startX = from.x; // left edge of source
                const startY = from.y + from.height / 2;
                const endX = to.x; // left edge of target
                const endY = to.y + to.height / 2;
                const horizOffset = Math.max(40, Math.abs(endY - startY) / 4);
                const cp1x = startX - horizOffset;
                const cp2x = endX - horizOffset;

                path = `M ${startX} ${startY} C ${cp1x} ${startY} ${cp2x} ${endY} ${endX} ${endY}`;
            }

        } else {
            // console.log("Rendering edge:", e);
            const startX = startCenterX;
            const startY = startBottomY;
            const endX = targetCenterX;
            const endY = targetTopY;
            const dy = Math.max(40, Math.abs(endY - startY) / 2);
            path = `M ${startX} ${startY} C ${startX} ${startY + dy} ${endX} ${endY - dy} ${endX} ${endY}`;
        }

        const key = `${e.from}-${e.to}`;
        const highlighted = highlightedEdges.has(key);
        return (
            <g key={key}>
                <path d={path}
                    fill="none"
                    stroke={highlighted ? '#004d40' : (e.conditional ? '#ff9800' : '#1976d2')}
                    strokeWidth={highlighted ? 4 : 2}
                    strokeDasharray={e.conditional ? '6 4' : undefined}
                    markerEnd="url(#arrow)" />
            </g>
        );
    };

    // handlers for pan/zoom
    // wheel zoom disabled per request
    const onWheel = (e) => { /* noop to disable scroll zoom */ };

    const onMouseDown = (e) => {
        dragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    };
    const onMouseMove = (e) => {
        if (!dragging.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPan({ x: dragStart.current.panX + dx, y: dragStart.current.panY + dy });
    };
    const onMouseUp = () => { dragging.current = false; };

    // highlight state: store selected edge keys
    const [highlightedEdges, setHighlightedEdges] = useState(new Set());
    const [selectedNodeId, setSelectedNodeId] = useState(null);

    const exportPNG = async () => {
        const w = Math.max(svgWidth, 100);
        const h = Math.max(svgHeight, 100);

        const escapeXml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const markerDef = `
          <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto" markerUnits="strokeWidth">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#1976d2" />
          </marker>
          <marker id="arrow-highlight" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto" markerUnits="strokeWidth">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#004d40" />
          </marker>
        `;

        // helper to compute the same edge path as on-screen
        const edgePath = (e) => {
            const from = nodeById.get(e.from);
            const to = nodeById.get(e.to);
            if (!from || !to) return '';
            const startCenterX = from.x + from.width / 2;
            const startBottomY = from.y + from.height;
            const targetCenterX = to.x + to.width / 2;
            const targetTopY = to.y;
            let path = '';
            if (targetTopY < startBottomY) {
                // mirror on-screen behavior: special 'backward' edges curve outward to the right
                if (e.action === 'backward') {
                    const startX = from.x + from.width;
                    const startY = from.y + from.height / 2;
                    const endX = to.x + to.width;
                    const endY = to.y + to.height / 2;
                    const dx = Math.abs(endX - startX);
                    const dy = Math.abs(endY - startY);
                    const horizOffset = Math.max(60, dy / 3, dx * 0.6);
                    const cp1x = startX + horizOffset;
                    const cp2x = endX + horizOffset;
                    path = `M ${startX} ${startY} C ${cp1x} ${startY} ${cp2x} ${endY} ${endX} ${endY}`;
                } else if (e.from === e.to) {
                    const endX = to.x;
                    const endY = to.y;
                    const startX = from.x;
                    const startY = from.y + from.height / 3;
                    // const endX = to.x + to.width;
                    // const endY = to.y + to.height;
                    const horizOffset = Math.max(40, Math.abs(endY - startY) / 4);
                    const cp1x = startX - horizOffset;
                    const cp2x = endX - horizOffset;

                    path = `M ${startX} ${startY} C ${cp1x} ${startY} ${cp2x} ${endY} ${endX} ${endY}`;
                } else {
                    const startX = from.x;
                    const startY = from.y + from.height / 2;
                    const endX = to.x;
                    const endY = to.y + to.height / 2;
                    const horizOffset = Math.max(40, Math.abs(endY - startY) / 4);
                    const cp1x = startX - horizOffset;
                    const cp2x = endX - horizOffset;
                    path = `M ${startX} ${startY} C ${cp1x} ${startY} ${cp2x} ${endY} ${endX} ${endY}`;
                }
            } else {
                const startX = startCenterX;
                const startY = startBottomY;
                const endX = targetCenterX;
                const endY = targetTopY;
                const dy = Math.max(40, Math.abs(endY - startY) / 2);
                path = `M ${startX} ${startY} C ${startX} ${startY + dy} ${endX} ${endY - dy} ${endX} ${endY}`;
            }
            return path;
        };

        // build SVG content
        const parts = [];
        parts.push('<?xml version="1.0" encoding="UTF-8"?>');
        parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">`);
        parts.push('<defs>');
        parts.push(markerDef);
        parts.push('</defs>');
        // background
        parts.push(`<rect width="100%" height="100%" fill="#ffffff" />`);

        // edges
        edges.forEach(e => {
            const d = edgePath(e);
            if (!d) return;
            const key = `${e.from}-${e.to}`;
            const highlighted = highlightedEdges.has(key);
            const stroke = highlighted ? '#004d40' : (e.conditional ? '#ff9800' : '#1976d2');
            const strokeWidth = highlighted ? 4 : 2;
            const dash = e.conditional ? 'stroke-dasharray="6 4"' : '';
            const marker = 'url(#arrow)';
            parts.push(`<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" ${dash} marker-end="${marker}" />`);
        });

        // nodes (simple rectangular representation) with role chips and action labels
        nodes.forEach(n => {
            const x = n.x;
            const y = n.y;
            const wN = n.width || 200;
            const hN = n.height || 96;
            // node background
            parts.push(`<g>`);
            parts.push(`<rect x="${x}" y="${y}" width="${wN}" height="${hN}" rx="8" ry="8" fill="#ffffff" stroke="#e0e0e0" />`);
            // title text (single line)
            const title = escapeXml(n.title || '');
            const textX = x + 12;
            const textY = y + 18;
            parts.push(`<text x="${textX}" y="${textY}" font-family="Segoe UI, Roboto, Helvetica, Arial, sans-serif" font-size="13" fill="#111">${title}</text>`);

            // roles (render up to 4 small chips)
            const step = stepById.get(n.id) || {};
            const roles = n.role || step.role || [];
            const roleStartX = x + 12;
            let rx = roleStartX;
            const roleY = y + 34;
            roles.slice(0, 4).forEach((r) => {
                const rText = escapeXml(String(r));
                const chipW = Math.min(wN - 24, Math.max(40, rText.length * 7 + 12));
                // if overflow, skip extra
                if (rx + chipW > x + wN - 12) return;
                parts.push(`<rect x="${rx}" y="${roleY}" rx="6" ry="6" width="${chipW}" height="18" fill="#e44f2269" stroke="#d8eee3" />`);
                parts.push(`<text x="${rx + 8}" y="${roleY + 13}" font-family="Segoe UI, Roboto, Helvetica, Arial, sans-serif" font-size="11" fill="#0b3">${rText}</text>`);
                rx += chipW + 6;
            });

            // actions (render up to 3 as small rounded rects with text) placed below roles
            const actionEntries = Object.entries(step.actions || {});
            const actionStartX = x + 12;
            const actionsYBase = y + 58; // leave room for roles
            actionEntries.slice(0, 3).forEach(([k, a], idx) => {
                const aTitle = escapeXml((a && a.title) ? a.title : k);
                // approximate width: 8px per char + padding
                const approxW = Math.min(wN - 24, Math.max(48, aTitle.length * 8 + 16));
                const btnX = actionStartX + idx * (approxW + 8);
                const btnY = actionsYBase;
                parts.push(`<rect x="${btnX}" y="${btnY}" rx="6" ry="6" width="${approxW}" height="18" fill="#f3f6ff" stroke="#c7d0ff" />`);
                parts.push(`<text x="${btnX + 8}" y="${btnY + 13}" font-family="Segoe UI, Roboto, Helvetica, Arial, sans-serif" font-size="11" fill="#0b3">${aTitle}</text>`);
            });

            parts.push(`</g>`);
        });

        parts.push('</svg>');
        const svgStr = parts.join('\n');

        const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ratio = window.devicePixelRatio || 1;
                canvas.width = w * ratio;
                canvas.height = h * ratio;
                const ctx = canvas.getContext('2d');
                ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0);
                URL.revokeObjectURL(url);
                const a = document.createElement('a');
                a.href = canvas.toDataURL('image/png');
                a.download = 'workflow.png';
                a.click();
            } catch (err) {
                console.error('Export image error', err);
            }
        };
        img.onerror = (err) => {
            console.error('Export image load error', err);
            URL.revokeObjectURL(url);
        };
        img.src = url;
    };

    const handleActionClick = (fromNodeId, actionKey) => {
        const step = stepById.get(fromNodeId);
        if (!step) return;
        const action = (step.actions || {})[actionKey];
        if (!action) return;
        const targets = [];
        if (action.next_step !== undefined) targets.push(action.next_step);
        if (action.previous_step !== undefined) targets.push(action.previous_step);

        // highlight only the direct edges from this node to the immediate targets
        const directEdges = new Set();
        edges.forEach(e => {
            if (e.from === fromNodeId && targets.includes(e.to)) directEdges.add(`${e.from}-${e.to}`);
        });
        setHighlightedEdges(directEdges);

        // pan to first target node (immediate next step)
        const finalNodeId = targets.length ? targets[0] : null;
        setSelectedNodeId(finalNodeId);
        if (finalNodeId && containerRef.current) {
            const target = nodeById.get(finalNodeId);
            // if (target) {
            //     const rect = containerRef.current.getBoundingClientRect();
            //     const targetPanX = rect.width / 2 - target.x - target.width / 2;
            //     const targetPanY = rect.height / 2 - target.y - target.height / 2;
            //     // animate pan from current pan to target over 300ms
            //     const start = { x: pan.x, y: pan.y };
            //     const duration = 300;
            //     const startTime = performance.now();
            //     const animate = (time) => {
            //         const t = Math.min(1, (time - startTime) / duration);
            //         const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOut
            //         const nx = start.x + (targetPanX - start.x) * ease;
            //         const ny = start.y + (targetPanY - start.y) * ease;
            //         setPan({ x: nx, y: ny });
            //         if (t < 1) requestAnimationFrame(animate);
            //     };
            //     requestAnimationFrame(animate);
            // }
        }
    };

    return (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 1, overflow: 'auto', position: 'relative' }}>
            <div style={{ position: 'relative', width: svgWidth, height: svgHeight, background: 'transparent' }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                ref={containerRef}>
                {/* Export control */}
                <div style={{ position: 'absolute', left: 8, top: 8, zIndex: 50 }}>
                    <Tooltip title="Export PNG">
                        <IconButton size="large" onClick={exportPNG} style={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: '50%' }}>
                            <DownloadIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                </div>
                <svg style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, zIndex: 20 }} width={svgWidth} height={svgHeight}>
                    <defs>
                        {/* standard arrow */}
                        <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto" markerUnits="strokeWidth">
                            <path d="M 0 0 L 10 5 L 0 10 z" fill="#1976d2" />
                        </marker>
                        {/* highlighted arrow */}
                        <marker id="arrow-highlight" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto" markerUnits="strokeWidth">
                            <path d="M 0 0 L 10 5 L 0 10 z" fill="#004d40" />
                        </marker>
                    </defs>
                    {edges.map(renderEdgePath)}
                </svg>
                <div style={{ position: 'absolute', left: 0, top: 0, transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', zIndex: 1 }}>
                    {nodes.map(n => {
                        const step = stepById.get(n.id) || {};
                        return (
                            <div key={n.id} style={{ position: 'absolute', left: n.x, top: n.y, width: n.width, height: n.height }}>
                                <StepNode title={"Step " + step.step_id + " | " + n.title} role={n.role} actions={step.actions} onActionClick={(k) => handleActionClick(n.id, k)} selected={selectedNodeId === n.id} />
                            </div>
                        );
                    })}
                </div>
            </div>
        </Box>
    );
}
